import React, { useEffect, useState } from 'react';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    Grid
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import UserTable from "../components/UserTable";
import UserModal from "../components/UserModal";
import { getUsers } from "../auth";
import { 
    People,
    AdminPanelSettings,
    SupervisorAccount,
    Person,
    CheckCircle
} from '@mui/icons-material';

interface User {
    id: number;
    email: string;
    role: string;
    isActive: boolean;
}

const UserManagement: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [selectedUser, setSelectedUser] = useState<User | null>(null);
    const [users, setUsers] = useState<User[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const fetchUsers = async () => {
        try {
            setLoading(true);
            setError(null);
            const response = await getUsers();
            setUsers(response as User[]);
        } catch (error) {
            setError("Erreur lors de la récupération des utilisateurs");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    const handleCreateUser = () => {
        setSelectedUser(null);
        setIsModalOpen(true);
    };

    const handleEditUser = (user: User) => {
        setSelectedUser(user);
        setIsModalOpen(true);
    };

    const getUserStats = () => {
        const total = users.length;
        const admins = users.filter(user => user.role === 'ADMIN').length;
        const managers = users.filter(user => user.role === 'MANAGER').length;
        const standardUsers = users.filter(user => user.role === 'USER').length;
        const active = users.filter(user => user.isActive).length;

        return { total, admins, managers, standardUsers, active };
    };

    const stats = getUserStats();

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                Gestion des utilisateurs
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                Gérez les comptes utilisateurs et leurs accès
                            </Typography>
                        </div>
                        <Button
                            variant="contained"
                            onClick={handleCreateUser}
                            startIcon={<AddIcon />}
                            className="bg-emerald-600 hover:bg-emerald-700 text-white shadow-sm hover:shadow-md transition-all duration-200 whitespace-nowrap"
                            sx={{
                                borderRadius: '8px',
                                textTransform: 'none',
                                px: 3,
                                py: 1
                            }}
                        >
                            Créer un utilisateur
                        </Button>
                    </Box>
                </Card>

                {/* Messages d'erreur */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}

                {/* Statistiques */}
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-gray-600 text-sm">Total</Typography>
                                    <Typography variant="h4" className="font-bold text-gray-800">
                                        {stats.total}
                                    </Typography>
                                </div>
                                <People className="text-gray-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-red-600 text-sm">Administrateurs</Typography>
                                    <Typography variant="h4" className="font-bold text-red-800">
                                        {stats.admins}
                                    </Typography>
                                </div>
                                <AdminPanelSettings className="text-red-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-blue-600 text-sm">Managers</Typography>
                                    <Typography variant="h4" className="font-bold text-blue-800">
                                        {stats.managers}
                                    </Typography>
                                </div>
                                <SupervisorAccount className="text-blue-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-emerald-600 text-sm">Utilisateurs</Typography>
                                    <Typography variant="h4" className="font-bold text-emerald-800">
                                        {stats.standardUsers}
                                    </Typography>
                                </div>
                                <Person className="text-emerald-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                    <Grid item xs={12} sm={6} lg={2.4}>
                        <Card className="bg-white/80 backdrop-blur-sm p-4 h-full">
                            <Box className="flex items-center justify-between">
                                <div>
                                    <Typography className="text-purple-600 text-sm">Actifs</Typography>
                                    <Typography variant="h4" className="font-bold text-purple-800">
                                        {stats.active}
                                    </Typography>
                                </div>
                                <CheckCircle className="text-purple-400 text-3xl" />
                            </Box>
                        </Card>
                    </Grid>
                </Grid>

                {/* Table des utilisateurs */}
                <Card className="bg-white/80 backdrop-blur-sm overflow-hidden">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="overflow-x-auto">
                            <UserTable
                                users={users}
                                onEditUser={handleEditUser}
                                refreshUsers={fetchUsers}
                            />
                        </Box>
                    )}
                </Card>

                {/* Modal de création/édition */}
                <UserModal
                    isOpen={isModalOpen}
                    onClose={() => setIsModalOpen(false)}
                    onSave={() => {
                        setIsModalOpen(false);
                        fetchUsers();
                    }}
                    user={selectedUser}
                />
            </Box>
        </MainLayout>
    );
};

export default UserManagement;